Vue.component('BlockImageBackground', {
  delimiters: ['{', '}'],
  props: {
    action_text: String,
    action_url: String,
    background_image: String,
    text: String,
    title: String
  },
  template: `
    <div class="block-image-background">
      <div class="block-background">
        <img :src="background_image" loading="lazy" />
      </div>
      <div class="block-content">
        <p class="h1">{title}</p>
        <p>{text}</p>
        <a class="h2" :href="action_url">{action_text}</a>
      </div>
    </div>
  `
})

Vue.component('BlockImageFeatured', {
  delimiters: ['{', '}'],
  props: {
    action_text: String,
    action_url: String,
    background_color: String,
    image: String,
    text: String,
    title: String
  },
  template: `
    <div class="block-image-featured" :style="{backgroundColor: background_color}">
      <div class="block-content">
        <p class="h1">{title}</p>
        <p>{text}</p>
        <a class="h2" :href="action_url">{action_text}</a>
      </div>
      <div class="block-image">
        <img :src="image" loading="lazy" />
      </div>
    </div>
  `
})

const ProductFragment = `fragment Product on Product {
  id
  handle
  title
  tags
  createdAt
  personalization: metafield (namespace: "options", key: "personalization") {
    value
  }
  collections (first: 10) {
    edges {
      node {
        id
        title
      }
    }
  }
  priceRange {
    minVariantPrice {
      amount
      currencyCode
    }
  }
  compareAtPriceRange {
    minVariantPrice {
      ...Price
    }
  }
  images (first: 2) {
    edges {
      node {
        altText
        id
        transformedSrc (maxWidth: 300, maxHeight: 300, crop: CENTER)
      }
    }
  }
}`

const BundlesMetafieldsFragment = `
fragment BundlesMetafields on Product {
  bundle: metafield (namespace: "bundle", key: "object") {
    id
    type
    reference {
      __typename
      ...Bundle
    }
  }
}

fragment Price on MoneyV2 {
  amount
  currencyCode
}

fragment Bundle on MetafieldReference {
  __typename
  ... on Metaobject {
    id
    type
    fields {
      key
      type
      value
      references (first: 5) {
        nodes {
          __typename
          # If we have a simple bundle, the products will be fields in the metaobject
          ... BundleItems
          # If we have a megabundle, we have another nested metaobject in a metafield
          ... on Metaobject {
            id
            type
            fields {
              key
              type
              value
              reference {
                ... BundleItems
              }
              references (first: 20) {
                nodes {
                  __typename
                  ... BundleItems
                }
              }
            }
          }
        }
      }
    }
  }
}

fragment BundleItems on MetafieldReference {
  __typename
  ... on Product {
    id
    priceRange {
      minVariantPrice {
        ...Price
      }
    }
    compareAtPriceRange {
      minVariantPrice {
        ...Price
      }
    }
  }
  ... on ProductVariant {
    id
    price {
      ...Price
    }
    compareAtPrice {
      ...Price
    }
    product {
      id
    }
  }
}
`

const productsQuery = `query Products ($after: String, $sortKey: ProductSortKeys!, $reverse: Boolean, $query: String) {
  products (first: 40, after: $after, sortKey: $sortKey, reverse: $reverse, query: $query) {
    pageInfo {
      hasNextPage
    }
    edges {
      cursor
      node {
        ...Product
        ...BundlesMetafields
      }
    }
  }
} ${ ProductFragment } ${ BundlesMetafieldsFragment }`

const collectionProductsQuery = `query Collection ($handle: String!, $after: String, $sortKey: ProductCollectionSortKeys!, $reverse: Boolean) {
  collection: collectionByHandle (handle: $handle) {
    products (first: 40, after: $after, sortKey: $sortKey, reverse: $reverse) {
      pageInfo {
        hasNextPage
      }
      edges {
        cursor
        node {
          ...Product
          ...BundlesMetafields
        }
      }
    }
  }
} ${ ProductFragment } ${ BundlesMetafieldsFragment }`

// Bundle helpers
/** Extract the multiplier field from the field map. */
function getMultiplier (fieldMap) {
  // ? Add support the mega bundle qty type?
  const field = fieldMap.get('multiplier') || fieldMap.get('quantity')
  return field && typeof field.value === 'string' ? Number(field.value) : 1
}

/** Extracts the list of products from the field map, and formats them to be used later. */
function getProducts (fieldMap) {
  const field = fieldMap.get('products') ?? fieldMap.get('product')
  const products = field && field.key === 'products' ? field && field.references && field.references.nodes : field && field.key === 'product' ? [field.reference] : []

  if (!products || !products.length) {
    return []
  }

  return products.flatMap((product) => {
    if (!product || product.__typename !== 'Product') {
      return []
    }

    const parsedPrice = product.priceRange.minVariantPrice.amount
    const parsedCompareAtPrice = product.compareAtPriceRange.minVariantPrice.amount

    return [{
      ...product,
      formatted: {
        price: Number(parsedPrice),
        compareAtPrice: Number(parsedCompareAtPrice),
      },
    }]
  })
}

/** Extracts a list of variants from the field map, groups them using their parent product ID, then formats them to be used later. */
function getVariants (fieldMap) {
  const field = fieldMap.get('product_variants') ?? fieldMap.get('variant')

  if (!field || !field.references || !field.references.nodes) {
    return []
  }

  const formattedVariants = field?.references?.nodes?.flatMap((variant) => {
    if (variant.__typename !== 'ProductVariant') {
      return []
    }

    const parsedPrice = variant.price.amount
    const parsedCompareAtPrice = variant.compareAtPrice?.amount || 0

    return [{
      ...variant,
      formatted: {
        price: Number(parsedPrice),
        compareAtPrice: Number(parsedCompareAtPrice),
      },
    }]
  })

  /**
   * Sometimes we only select a few variants from a product, instead of the whole product.
   * In that case, we only want to include one variant, so we group them by their parent product ID.
   */
  const groupedProductVariants = formattedVariants.reduce((group, variant) => {
    const matching = group.get(variant.product.id)

    // If we have a match and the matching min price is less than the current variant, just return
    if (matching && matching.formatted.price <= variant.formatted.price) {
      return group
    }

    // Otherwise, set it to the current variant
    return group.set(variant.product.id, variant)
  }, new Map())

  return Array.from(groupedProductVariants.values())
}


function calculateTotals ({ products, productVariants, multiplier }) {
  /**
   * The products + productVariants now have the same `minPrice` and `minComparePrice` fields
   * at their root, so we can merge into an array and map over all of them for the total.
   */
  const subtotals = [...products, ...productVariants].reduce((totals, item) => {
    return {
      price: totals.price + item.formatted.price,
      compareAtPrice: totals.compareAtPrice + item.formatted.compareAtPrice,
    }
  }, {
    price: 0,
    compareAtPrice: 0,
  })

  const priceTotal = subtotals.price * multiplier
  const compareAtPriceTotal = subtotals.compareAtPrice * multiplier

  return {
    price: priceTotal,
    compareAtPrice: compareAtPriceTotal,
  }
}


function handleSimpleBundleTotals (bundle) {
  const fieldMap = new Map(bundle.fields.map(field => [field.key, field]))
  const multiplier = getMultiplier(fieldMap)
  const products = getProducts(fieldMap)
  const productVariants = getVariants(fieldMap)
  return calculateTotals({ products, productVariants, multiplier })
}

function handleMegaBundleTotals (bundle) {
  const items = bundle.fields.find(field => field.key === 'items')

  if (!items || !items.references || !items.references.nodes) {
    console.warn('[Bundles] Missing items field from megabundle')

    return {
      compareAtPrice: 0,
      // Set estimated base price
      price: 120,
    }
  }

  const formattedItems = items.references.nodes.flatMap((item) => {
    if (item.__typename !== 'Metaobject') {
      return []
    }

    // Now we have the same base type, we can re-use our simple bundles code
    return handleSimpleBundleTotals(item)
  })

  return formattedItems.reduce((totals, item) => {
    return {
      price: totals.price + item.price,
      compareAtPrice: totals.compareAtPrice + item.compareAtPrice,
    }
  }, {
    price: 0,
    compareAtPrice: 0,
  })
}

/**
 * Given a product that has a bundle metafield, calculate the total prices for all items within the bundle -
 * regardless of whether it is a simple or mega bundle type.
 */
function computeBundlePricing (bundle) {
  if (!bundle || !bundle.reference || bundle.reference.__typename !== 'Metaobject') {
    console.warn('[Bundles] Input passed was not a metaobject type')
    return
  }

  if (bundle.reference.type === 'bundle') {
    return handleSimpleBundleTotals(bundle.reference)
  }

  if (bundle.reference.type === 'megabundle') {
    return handleMegaBundleTotals(bundle.reference)
  }

  console.warn('[Bundles] No bundle pricing handler found for type', bundle.type)
}

// App

const storefrontAPIEndpoint = new URL(window.location.href)
storefrontAPIEndpoint.pathname = '/api/2023-01/graphql.json'

const storefrontToken = window.__initialData__ && window.__initialData__.storefrontToken

async function storefront (query, variables = {}) {
  if (!storefrontToken) throw new Error('No storefrontToken provided.')
  if (!query) throw new Error('No query provided.')

  const excludedQuery = '(NOT tag:hidden-products NOT tag:hidden NOT tag:hidden-view-all)'
  const extendedQuery = variables.query ? `${ excludedQuery } AND (${ variables.query })` : excludedQuery

  const response = await fetch(storefrontAPIEndpoint.toString(), {
    method: 'POST',
    headers: {
      'X-Shopify-Storefront-Access-Token': storefrontToken,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      query,
      variables: {
        ...variables,
        query: extendedQuery
      }
    })
  })

  if (!response.ok) throw new Error(await response.text())
  const { data, errors } = await response.json()
  if (errors) throw new Error(errors[0].message)

  return data
}

const sortOptions = new Map([
  ['best-selling', {
    sortKey: 'BEST_SELLING'
  }],
  ['created', {
    sortKey: 'CREATED_AT',
    reverse: true
  }],
  ['title-az', {
    sortKey: 'TITLE'
  }],
  ['title-za', {
    sortKey: 'TITLE',
    reverse: true
  }],
  ['price-hl', {
    sortKey: 'PRICE',
    reverse: true
  }],
  ['price-lh', {
    sortKey: 'PRICE'
  }]
])

// Get initial options from search params
const searchParams = new URLSearchParams(window.location.search)
const initialOptions = { sort: 'title-az', search: '', collection: '', personalization: false, tags: [] }
searchParams.forEach((value, key) => {
  if (key === 'tags') initialOptions[key] = value.split(',').filter(Boolean)
  else initialOptions[key] = value
})

const defaultChainProduct = window.__initialData__ && window.__initialData__.products && window.__initialData__.products.defaultChainProduct

window.productFilterApp = new Vue({
  el: '#collection',
  delimiters: ['${', '}'],
  data: () => ({
    state: {
      loading: true,
      error: false
    },
    blocks,
    products: [],
    pageInfo: {
      hasNextPage: true
    },
    currentQuery: productsQuery,
    variables: {
      sortKey: 'BEST_SELLING',
      query: 'NOT tag:hidden-products NOT tag:hidden NOT tag:hidden-view-all'
    },
    mobileActive: {
      filters: false,
      sort: false
    },
    active: {
      school: true,
      organization: false,
      collection: false,
      collaboration: false,
      custom: true
    },
    listFilters: {
      school: null,
      organization: null
    },
    customFilters: {
      personalization: initialOptions.personalization
    },
    options: initialOptions,
    timeout: null
  }),
  computed: {
    search: {
      get () {
        return this.options.search
      },
      set (val) {
        this.options.collection = ''
        if (this.timeout) clearTimeout(this.timeout)
        this.timeout = setTimeout(() => {
          this.options.search = val
        }, 500)
      }
    },
    schools () {
      const letter = this.listFilters.school
      if (!letter) return schoolFilters
      return schoolFilters.filter(([name]) => name.toLowerCase().charAt(0) === letter)
    },
    organizations () {
      const letter = this.listFilters.organization
      if (!letter) return organizationFilters
      return organizationFilters.filter(([name]) => name.toLowerCase().charAt(0) === letter)
    },
    collections () {
      return [
        {
          title: 'By School',
          key: 'school',
          action: schoolAction,
          letters: schoolLetters.filter(c => !!c),
          data: this.schools
        },
        {
          title: 'By collection',
          key: 'collection',
          data: collectionFilters
        },
        {
          title: 'Greek Organization',
          key: 'organization',
          action: organizationAction,
          letters: organizationLetters.filter(c => !!c),
          data: this.organizations
        },
        {
          title: 'By collaboration',
          key: 'collaboration',
          data: collaborationFilters
        }
      ]
    },
    formattedTagFilters () {
      return filterTags.flatMap(tag => {
        const tags = tag.tags.split(',').map(str => str.trim().toLowerCase())
        if (!tags.length) return []

        const active = tags.every(tag => this.options.tags.includes(tag))

        return [{ ...tag, tags, active }]
      })
    },
    filteredProducts () {
      const filters = Object.entries(this.customFilters).filter(([name, value]) => value)
      const products = this.products

      const filtered = products.filter(product => {
        return filters.every(([key]) => {
          return product[key] && product[key].value === 'Enabled'
        })
      })

      return filtered
    }
  },
  watch: {
    'customFilters.personalization' (personalization) {
      this.options.personalization = personalization
      if (personalization) {
        this.listFilters = { school: null, organization: null }
        this.options.collection = null
      }
    },
    options: {
      deep: true,
      immediate: true,
      async handler (options) {
        // Set location params
        const searchParams = new URLSearchParams()
        Object.entries(options).forEach(([key, value]) => value && searchParams.set(key, value))
        window.history.replaceState(null, document.title, `?${ searchParams.toString() }`)

        // Remove all products to show loading
        this.products = []

        // And scroll to top of page
        window.scrollTo({ top: 0, behavior: 'smooth' })

        const sort = sortOptions.get(options.sort)// Get the relevant search params
        this.query = productsQuery // Set initial query

        const queries = []

        // Set search text
        const searchQuery = options.search ? `title:*${ options.search }*` : ''
        queries.push(searchQuery)

        // Tags
        const tagFilterQuery = options.tags.map(tag => `tag:${ tag } `).join(' OR ')
        queries.push(`(${ tagFilterQuery })`)

        let variables = { // Set default variables
          ...sort,
          query: queries.join(' AND ')
        }

        if (options.collection && !options.search) {
          this.query = collectionProductsQuery
          variables = {
            ...variables,
            // Shopify annoyingly uses different values for product & collection.product sort keys
            sortKey: variables.sortKey === 'CREATED_AT' ? 'CREATED' : variables.sortKey,
            handle: options.collection
          }
        }

        // Store the variables, for future pagination requests
        this.variables = variables

        const newProducts = await this.fetchProducts(variables)
        this.products = newProducts
      }
    }
  },
  created () {
    new Glider(document.querySelector('.mobile-blocks'), {
      slidesToShow: 1,
      slidesToScroll: 1,
      draggable: true
    })
  },
  async mounted () {
    //  Fetch initial products, in default order
    //     const products = await this.fetchProducts(this.variables)
    //     this.products = products

    // Start infinite scroll
    this.$nextTick(() => this.infiniteScroll())
  },
  methods: {
    infiniteScroll () {
      const el = this.$refs.infinite

      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) return this.fetchMore()
        })
      })

      observer.observe(el)
    },
    formatCollectionName (name) {
      return name.replace('new', '<span class="new">new</span>')
    },
    formatProduct ({ cursor, node }) {
      const bundlePricing = node.bundle && computeBundlePricing(node.bundle)
      const price = bundlePricing ? bundlePricing.price : node.priceRange.minVariantPrice.amount
      const compareAtPrice = bundlePricing ? bundlePricing.compareAtPrice : node.compareAtPriceRange.minVariantPrice.amount

      const daysInMs = 120 * 24 * 60 * 60 * 1000

      const createdAtMs = new Date(node.createdAt).getTime()
      const nowMs = new Date().getTime()
      const isNew = (nowMs - createdAtMs) <= daysInMs

      return {
        ...node,
        price,
        compareAtPrice,
        isNew,
        cursor,
        tags: node.tags.map(tag => tag.toLowerCase()),
        images: node.images.edges.map(({ node }) => node)
      }
    },
    async fetchProducts (variables) {
      this.state.loading = true
      let { products, collection } = await storefront(this.query, variables)
      this.state.loading = false

      if (!products && !collection) return []
      if (collection) products = collection.products

      this.pageInfo = products.pageInfo
      return products.edges.map(this.formatProduct)
    },
    async fetchMore () {
      if (this.state.loading || !this.pageInfo.hasNextPage) return

      const last = this.products[this.products.length - 1]
      const newProducts = await this.fetchProducts({
        after: last.cursor,
        ...this.variables
      })

      this.products = [...this.products, ...newProducts]
    },
    setCollectionFilter (type, handle) {
      if (this.options.collection === handle) this.options.collection = null
      else this.options.collection = handle

      this.options.search = ''
    },
    setListFilter (type, letter) {
      const current = this.listFilters[type]
      if (current === letter) this.listFilters[type] = null
      else this.listFilters[type] = letter

      this.options.search = ''
    },
    setCustomFilter (type) {
      this.customFilters[type] = !this.customFilters[type]
      this.options.search = ''
    },
    setTagFilter (tags) {
      this.options.tags = tags
    },
    formatPrice (amount) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)
    }
  }
})
